import { Container, GlobalStyles } from '@mui/material';
import { Outlet, useLocation } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useDragDropManager } from 'react-dnd';

import parseJWT from '@@auth/utils/parseJWT';
import MenuBar from '@@components/MenuBar';
import useLastRouteTracker from '@@containers/LastRouteTracker/useLastRouteTracker';
import Navigation from '@@containers/Navigation/Navigation';
import { useAuthStore, useIsAuthenticated } from '@@scripts/store/useAuthStore';
import { useDevToolbarMode } from '@@scripts/store/useDevToolbarStore';
import CycleRegionShortcut from '@@shortcuts/components/CycleRegionShortcut';
import ShortcutsDialog from '@@shortcuts/components/ShortcutsDialog';
import { addDataDogRUMAction, setDataDogRUMUserId } from '@@utils/dataDog';

import AppNotifications from './AppNotifications';
import PageLayout, {
    PageLayoutContent,
    PageLayoutFooter,
    PageLayoutHeader,
    PageLayoutNavigation,
    PageLayoutNotificationBar,
    PageLayoutSidebar,
    PageLayoutToolbar,
} from './PageLayout/PageLayout';
import PageTitle from './PageTitle';
import DeveloperToolbar from '../DeveloperToolbar/DeveloperToolbar';

const VITE_PRELOAD_ERROR_RELOAD_KEY = 'vite_preload_error_reloaded';

// Important note: It is important to render global styles globally. Rendering them in a nested component, can lead to
// performance issues! This is what we've experienced for the insert toolbar of the richt text editor, which became
// a major incident!
// https://tamedia.atlassian.net/browse/CD2-8379
const StyledDndGlobalStyles = `
    body.dragging {
        // When a user drags an item to the bottom of the browser window, the default behaviour is to scroll downwards on the
        // page (which makes sense, in order to drop the dragged item somewhere further down on the page).
        // MenuBar prevented the user from doing so, because it kind of blocked the drag event from being propagated to
        // the scrollable area. In order to not block this, we disable 'pointer-events' while the user is dragging. This
        // fixes the problem.
        // Sidenote: 'useDragBlock.ts' adds the 'dragging' class to the body when a user starts to drag a rich text editor
        // element.
        ${MenuBar} {
            pointer-events: none;
        }
    }
`;

const DndGlobalStyles = () => {
    const dndManager = useDragDropManager();

    useEffect(() => {
        const unsubscribe = dndManager.getMonitor().subscribeToStateChange(() => {
            if (dndManager.getMonitor().isDragging()) {
                document.body.classList.add('dragging');
            } else {
                document.body.classList.remove('dragging');
            }
        });

        return () => {
            unsubscribe();
        };
    }, []);

    return <GlobalStyles styles={StyledDndGlobalStyles} />;
};

export const pathsWithoutPageLayout = ['/isolated', '/plate'];

export const pathsWithoutNavigation = ['/preview'];

const matchesOneOfPaths = (paths: string[], pathname: string) => {
    const lowerCasePathname = pathname.toLowerCase();

    return paths.some(
        (path) => lowerCasePathname === path || lowerCasePathname.startsWith(`${path}/`),
    );
};

const AppWrapper = () => {
    const pathname = useLocation({ select: (location) => location.pathname });

    useLastRouteTracker();

    // Vite fires this event when a dynamic import (lazy route) fails - typically because a new
    // deployment replaced the hashed chunk filenames. Reloading fetches the new index.html and
    // resolves the missing chunks. The sessionStorage key stores the build VERSION to allow one
    // reload per deployment while preventing infinite reload loops.
    useEffect(() => {
        const handleVitePreloadError = () => {
            const currentVersion = VERSION ?? BUILD_TIMESTAMP ?? 'unknown';
            const reloadedVersion = sessionStorage.getItem(VITE_PRELOAD_ERROR_RELOAD_KEY);

            if (reloadedVersion === currentVersion) {
                // Already reloaded once for this deployment - don't loop. Let the error surface naturally.
                return;
            }

            sessionStorage.setItem(VITE_PRELOAD_ERROR_RELOAD_KEY, currentVersion);
            window.location.reload();
        };

        window.addEventListener('vite:preloadError', handleVitePreloadError);

        return () => {
            window.removeEventListener('vite:preloadError', handleVitePreloadError);
        };
    }, []);

    const isAuthenticated = useIsAuthenticated();
    const devToolbar = useDevToolbarMode();
    const accessToken = useAuthStore((state) => state.accessToken);

    // In order to avoid using acceptance environments when not necessary
    // We revert to default ones when token expires
    useEffect(() => {
        if (accessToken && !isAuthenticated) {
            localStorage.removeItem('CDII_API');
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated && accessToken) {
            const jwt = parseJWT(accessToken);

            const username = jwt && jwt.user_name ? jwt.user_name : 'unknown';

            setDataDogRUMUserId(username);
        }
    }, [isAuthenticated, accessToken]);

    useEffect(() => {
        const reducedMotionEnabled = window.matchMedia?.(
            '(prefers-reduced-motion: reduce)',
        ).matches;

        if (reducedMotionEnabled) {
            addDataDogRUMAction('reducedMotionInfo', { reducedMotionEnabled });
        }
    }, []);

    const isPathWithoutPageLayout = matchesOneOfPaths(pathsWithoutPageLayout, pathname);
    const isPathWithoutNavigation = matchesOneOfPaths(pathsWithoutNavigation, pathname);

    return (
        <Container disableGutters fixed sx={{ display: 'flex', minHeight: '100%' }}>
            <DndGlobalStyles />

            <PageTitle />

            {isAuthenticated && !isPathWithoutPageLayout ? (
                <>
                    <PageLayout>
                        <PageLayoutNavigation as="nav">
                            {!isPathWithoutNavigation && <Navigation />}
                        </PageLayoutNavigation>

                        <PageLayoutNotificationBar>
                            <AppNotifications />
                        </PageLayoutNotificationBar>

                        <PageLayoutHeader />

                        <PageLayoutSidebar as="aside" />

                        <PageLayoutContent as="main">
                            <Outlet />
                        </PageLayoutContent>

                        <PageLayoutToolbar />

                        <PageLayoutFooter />
                    </PageLayout>

                    {devToolbar && <DeveloperToolbar />}

                    <ShortcutsDialog />
                    <CycleRegionShortcut />
                </>
            ) : (
                <Outlet />
            )}
        </Container>
    );
};

export default AppWrapper;
